import {
  DividerComponent,
  PaperComponent,
  TextFieldComponent,
} from "@/components";
import { ChipsGroup } from "@/components/ChipsGroup";
import { IMAGES, WEB_COLORS } from "@/lib/constants";
import { useGetMandateModesQuery, useVpaListQuery } from "@/lib/redux";
import { Box, FormControlLabel, Radio, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useMemo } from "react";
import styles from "../repayment.module.css";

const UPI_MANDATE_STEPS = [
  {
    label: "Step 1:",
    description: "Scan and approve the QR code to verify your bank account.",
  },
  {
    label: "Step 2:",
    description:
      "Scan and approve the QR code again to register your UPI Mandate.",
  },
];

const AA_UPI_MANDATE_STEPS = [
  {
    label: "Step 1:",
    description: "Scan and approve the QR code to register your UPI Mandate.",
  },
];

const PaymentMethods = ({
  formik,
  appId,
  openUpiInput,
  verificationMode,
  isRpl = false,
}) => {
  const t = useTranslations("repaymentSetUp.repaymentMode");
  const { data: mandateModes } = useGetMandateModesQuery(
    { appId },
    { skip: !appId },
  );

  const mandateModesOptions = useMemo(() => {
    // checks for backward compatibility
    if (Array.isArray(mandateModes?.data)) return mandateModes?.data;
    else if (Array.isArray(mandateModes)) {
      return mandateModes;
    } else return [];
  }, [mandateModes]);

  const { data: vpaList } = useVpaListQuery();
  function splitPaymentMethod(method) {
    if (method === "debitCard") return t("debitcard");
    if (method === "netbanking") return t("netbanking");
    if (method === "upi") return "UPI";
    return method;
  }

  const options = useMemo(() => vpaList || [], [vpaList]);
  const upiChipsEligible =
    typeof formik.values.upiId === "string" &&
    /^[a-zA-Z0-9._-]{1,45}@/.test(formik.values.upiId);

  return mandateModesOptions
    ?.filter((i) => i?.visible)
    ?.map(({ mode, maxEmiAmount }) => (
      <Box
        key={mode}
        sx={{
          my: 2,
          py: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          position: "relative",
          borderRadius: { xs: "8px", md: "20px" },
          width: "100%",
          border:
            formik?.values?.modeOfPayment === mode
              ? "1px solid #7F9893"
              : "1px solid #CCD6D4",

          "&:hover": {
            border: `1px solid ${WEB_COLORS.primary}`,
          },

          background:
            formik?.values?.modeOfPayment === mode
              ? "#E5F1F1"
              : "rgba(242, 247, 247, 0.6)",
        }}
      >
        <FormControlLabel
          sx={{
            width: "100%",
            height: "100%",
            textTransform: "capitalize",
          }}
          control={<Radio />}
          label={splitPaymentMethod(mode)}
          value={mode}
        />
        {mode === "debitCard" && (
          // <Typography
          // className={`${styles["tag"]} ${styles["recommended-tag"]}`}
          //   sx={{ alignItems: "center" }}
          // >
          //   <Image
          //     className={styles["recommended-tag"]}
          //     src={IMAGES.greenStar}
          //     alt="star"
          //     height={24}
          //     width={24}
          //   />
          //   {t("popularchoice")}
          // </Typography>
          <Image
            src={IMAGES.popularChoice}
            alt="Popular choice"
            height={35}
            width={125}
            className={`${styles["tag"]} ${styles["recommended-tag"]}`}
          />
        )}
        {openUpiInput &&
          formik?.values?.modeOfPayment === "upi" &&
          mode === "upi" && (
            <Stack mt={1.5} gap={1}>
              <TextFieldComponent
                name="upiId"
                label="Enter your UPI ID"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.upiId}
                error={Boolean(formik.touched.upiId && formik.errors.upiId)}
                errorMessage={formik.touched.upiId && formik.errors.upiId}
              />
              {upiChipsEligible && Boolean(options?.length) ? (
                <ChipsGroup
                  options={options}
                  containerSx={{
                    mb: 2,
                    mt: 1.5,
                    width: "100%",
                    maxWidth: "1055px",
                  }}
                  value={
                    formik.values.upiId &&
                    formik.values.upiId.includes("@") &&
                    formik.values.upiId?.slice(
                      formik.values.upiId?.indexOf("@"),
                    )
                  }
                  sx={{
                    border: `1px solid ${WEB_COLORS.fadeColor}50`,
                    pl: 0.8,
                    pr: 1,
                    bgcolor: "transparent",
                  }}
                  onClick={(e) => {
                    let currentValue = formik.values.upiId || "";
                    const atIndex = currentValue.indexOf("@");
                    if (atIndex !== -1) {
                      currentValue = currentValue.slice(0, atIndex);
                    }
                    const newValue = currentValue + e.target.value;
                    formik.setFieldValue("upiId", newValue);
                  }}
                />
              ) : (
                <Typography
                  fontSize={{ md: 14, xs: 12 }}
                  fontWeight={{ md: 400, xs: 350 }}
                  color={WEB_COLORS.fadeColor}
                >
                  Please follow the format of{" "}
                  <Typography
                    component="span"
                    sx={{ fontWeight: 500, color: WEB_COLORS.darkFadedText }}
                  >
                    username@upi
                  </Typography>{" "}
                  for entering your UPI ID
                </Typography>
              )}
              <DividerComponent />
              <Stack direction={"row"} spacing={2}>
                <Image src={IMAGES.noteLight} alt="note" />
                <Stack>
                  <Typography
                    fontSize={{ md: 14, xs: 12 }}
                    color={WEB_COLORS.darkTeal}
                    fontWeight={{ md: 400, xs: 350 }}
                  >
                    {t("upinote", {
                      amount: maxEmiAmount,
                    })}
                  </Typography>
                  <Typography
                    fontSize={{ md: 14, xs: 12 }}
                    fontWeight={{ md: 400, xs: 350 }}
                    color={WEB_COLORS.darkTeal}
                  >
                    Don't worry! Only your{" "}
                    <Typography
                      component="span"
                      sx={{ fontWeight: 500, color: WEB_COLORS.darkTeal }}
                    >
                      EMI
                    </Typography>{" "}
                    will be deducted
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          )}

        {formik?.values?.modeOfPayment === "upi" &&
          mode === "upi" &&
          !openUpiInput && (
            <Stack mt={1.5} gap={1}>
              <Typography
                fontSize={{ md: 16, xs: 14 }}
                fontWeight={{ md: 500, xs: 500 }}
                color={WEB_COLORS.darkTeal}
              >
                Scan QR Code
              </Typography>
              <Typography
                fontSize={{ md: 14, xs: 12 }}
                fontWeight={{ md: 400, xs: 350 }}
                color={WEB_COLORS.darkTeal}
              >
                You&apos;ll need to scan and approve the UPI QR code{" "}
                {!["AA", "PERFIOS"].includes(verificationMode) &&
                  isRpl === false && <span>twice</span>}{" "}
                to set up your UPI Mandate.
              </Typography>
              {!["AA", "PERFIOS"].includes(verificationMode) &&
              isRpl === false ? (
                <Stack gap={0.5}>
                  {UPI_MANDATE_STEPS.map(({ label, description }) => (
                    <Typography
                      key={label}
                      fontSize={{ md: 14, xs: 12 }}
                      fontWeight={{ md: 400, xs: 350 }}
                      color={WEB_COLORS.darkTeal}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 500, color: WEB_COLORS.darkTeal }}
                      >
                        {label}
                      </Typography>{" "}
                      {description}
                    </Typography>
                  ))}
                  <Typography
                    fontSize={{ md: 14, xs: 12 }}
                    fontWeight={{ md: 400, xs: 350 }}
                    color={WEB_COLORS.darkTeal}
                  >
                    Please complete both requests to proceed.
                  </Typography>
                </Stack>
              ) : (
                <Stack gap={0.5}>
                  {AA_UPI_MANDATE_STEPS.map(({ label, description }) => (
                    <Typography
                      key={label}
                      fontSize={{ md: 14, xs: 12 }}
                      fontWeight={{ md: 400, xs: 350 }}
                      color={WEB_COLORS.darkTeal}
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 500, color: WEB_COLORS.darkTeal }}
                      >
                        {label}
                      </Typography>{" "}
                      {description}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
      </Box>
    ));
};

export default PaymentMethods;
